
'use client';

import { createContext, useContext, useState, ReactNode, useEffect, useCallback } from 'react';
import type { Post, Comment, ReactionType, NewPost, Friend } from '@/types';
import { db, storage } from '@/lib/firebase';
import { collection, onSnapshot, query, Timestamp, addDoc, serverTimestamp, doc, deleteDoc, updateDoc, arrayUnion, increment, getDoc, orderBy, where } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from './AuthContext';
import { v4 as uuidv4 } from 'uuid';
import type { User } from 'firebase/auth';
import { useParams } from 'next/navigation';

interface PortfolioContextType {
    posts: Post[];
    createPost: (post: NewPost, author: User | Friend | null) => Promise<void>;
    deletePost: (postId: string) => Promise<void>;
    updatePost: (postId: string, newContent: string) => Promise<void>;
    addComment: (postId: string, commentContent: string) => Promise<void>;
    handleReaction: (postId: string, reaction: ReactionType) => Promise<void>;
}

const PortfolioContext = createContext<PortfolioContextType | undefined>(undefined);

const safeGetDate = (timestamp: any): Date => {
    if (timestamp instanceof Timestamp) return timestamp.toDate();
    if (timestamp instanceof Date) return timestamp;
    return new Date();
};

export const PortfolioProvider = ({ children }: { children: ReactNode }) => {
    const [posts, setPosts] = useState<Post[]>([]);
    const { toast } = useToast();
    const { user, isOwner } = useAuth();
    const params = useParams();
    const artistId = params.id as string;

    useEffect(() => {
        if (!artistId) return;
        
        const q = query(collection(db, "posts"), where("author.handle", "==", artistId), orderBy("timestamp", "desc"));
        const unsubscribe = onSnapshot(q, (querySnapshot) => {
            const postsData: Post[] = [];
            querySnapshot.forEach((doc) => {
                const data = doc.data();
                postsData.push({
                    id: doc.id,
                    author: data.author,
                    content: data.content,
                    timestamp: safeGetDate(data.timestamp),
                    comments: Array.isArray(data.comments) ? data.comments.map((c: any) => ({ ...c, timestamp: safeGetDate(c.timestamp)})) : [],
                    likes: data.likes || 0,
                    reaction: data.reaction || null,
                    category: data.category || null,
                    imageUrls: data.imageUrls || [],
                    imagePaths: data.imagePaths || [],
                    sketchfabEmbedUrl: data.sketchfabEmbedUrl || null,
                    isVIP: data.isVIP || false,
                    vipStyle: data.vipStyle || 'style1',
                });
            });
            setPosts(postsData);
        }, (error) => {
            console.error("Firestore snapshot error (portfolio):", error);
            toast({
                title: "Connection Error",
                description: "Could not load the portfolio.",
                variant: "destructive",
            });
        });

        return () => unsubscribe();
    }, [toast, artistId]);

    const createPost = async (newPost: NewPost, author: User | Friend | null) => {
        if (!isOwner || !author) {
            toast({ title: "Not Authorized", description: "You do not have permission to post.", variant: "destructive" });
            return;
        }
        if (!newPost.content && (!newPost.images || newPost.images.length === 0) && !newPost.sketchfabEmbedUrl) {
            toast({ title: "Empty Post", description: "Please add some content.", variant: "destructive" });
            return;
        }
        try {
            let imageUrls: string[] = [];
            let imagePaths: string[] = [];
            
            const authorId = 'uid' in author ? author.uid : author.id;

            if (newPost.images && newPost.images.length > 0) {
                const uploadPromises = Array.from(newPost.images).map(async (imageFile) => {
                    const imageId = uuidv4();
                    const imagePath = `posts/${authorId}/${imageId}`;
                    const imageRef = ref(storage, imagePath);
                    await uploadBytes(imageRef, imageFile);
                    const downloadUrl = await getDownloadURL(imageRef);
                    imagePaths.push(imagePath);
                    return downloadUrl;
                });
                imageUrls = await Promise.all(uploadPromises);
            }
            
            const authorName = 'displayName' in author ? author.displayName : author.name;
            const authorAvatar = 'photoURL' in author ? author.photoURL : author.avatarUrl;

            const postData: any = {
                content: newPost.content,
                category: newPost.category || null,
                sketchfabEmbedUrl: newPost.sketchfabEmbedUrl || null,
                author: { 
                    name: authorName || 'Anonymous User', 
                    avatarUrl: authorAvatar || 'https://placehold.co/40x40.png', 
                    handle: authorId 
                },
                timestamp: serverTimestamp(),
                likes: newPost.initialLikes || 0,
                comments: [],
                reaction: null,
                imageUrls: imageUrls,
                imagePaths: imagePaths,
                isVIP: newPost.isVIP || false,
            };

            if (newPost.isVIP) {
                postData.vipStyle = newPost.vipStyle;
            }

            await addDoc(collection(db, "posts"), postData);

        } catch (error) {
            console.error("Error creating post: ", error);
            toast({ title: "Post Error", description: "The post could not be published.", variant: "destructive" });
        }
    };

    const deletePost = async (postId: string) => {
        if (!isOwner) return;
        try {
            const postRef = doc(db, "posts", postId);
            const postSnap = await getDoc(postRef);
            if (postSnap.exists()) {
                const postData = postSnap.data();
                if (postData.imagePaths && postData.imagePaths.length > 0) {
                    await Promise.all(postData.imagePaths.map((path: string) => deleteObject(ref(storage, path))));
                }
            }
            await deleteDoc(postRef);
        } catch (error) {
            console.error("Error deleting post: ", error);
            toast({ title: "Deletion Error", variant: "destructive" });
        }
    };

    const updatePost = async (postId: string, newContent: string) => {
       if (!isOwner) return;
       try {
            await updateDoc(doc(db, "posts", postId), { content: newContent });
        } catch (error) {
            console.error("Error updating document: ", error);
            toast({ title: "Update Error", variant: "destructive" });
        }
    };
    
    const addComment = async (postId: string, commentContent: string) => {
        if (!user) {
            toast({ title: "Not Authenticated", variant: "destructive" });
            return;
        }
        try {
            const newComment: Comment = {
                author: {
                    name: user.displayName || 'Anonymous',
                    avatarUrl: user.photoURL || 'https://placehold.co/40x40.png',
                    handle: user.uid
                },
                content: commentContent,
                timestamp: new Date()
            };
            await updateDoc(doc(db, "posts", postId), {
                comments: arrayUnion(newComment)
            });
        } catch (error) {
            console.error("Error adding comment: ", error);
            toast({ title: "Comment Error", variant: "destructive" });
        }
    };

    const handleReaction = async (postId: string, reaction: ReactionType) => {
        if (!user) return;
        try {
            const postRef = doc(db, "posts", postId);
            const postDoc = await getDoc(postRef);
            if (!postDoc.exists()) return;

            const currentReaction = postDoc.data().reaction;
            if (currentReaction === reaction) {
                await updateDoc(postRef, { reaction: null, likes: increment(-1) });
            } else if (currentReaction && reaction) {
                 await updateDoc(postRef, { reaction: reaction });
            } else if (!currentReaction && reaction) {
                 await updateDoc(postRef, { reaction: reaction, likes: increment(1) });
            } else if (currentReaction) {
                await updateDoc(postRef, { reaction: null, likes: increment(-1) });
            }
        } catch (error) {
            console.error("Error handling reaction: ", error);
             toast({ title: "Reaction Error", variant: "destructive" });
        }
    }

    return (
        <PortfolioContext.Provider value={{ posts, createPost, deletePost, updatePost, addComment, handleReaction }}>
            {children}
        </PortfolioContext.Provider>
    );
};

export const usePortfolio = () => {
    const context = useContext(PortfolioContext);
    if (context === undefined) {
        throw new Error('usePortfolio must be used within a PortfolioProvider');
    }
    return context;
};
